// VehicleRequests — lists pending vendor-submitted vehicle requests and lets
// an operator fulfil (via make→model→entry cascade picker) or reject (with a
// reason) each one.
//
// Mirrors NeedsLinking.tsx conventions: same data-fetch/auth client, same
// make→model→entry cascade helpers, same component/styling idioms, same
// loading/error handling patterns.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PageHeader, TablePager } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  fetchCatalogMakes,
  fetchCatalogModels,
  fetchCatalogEntries,
  type CatalogMakeRow,
  type CatalogModelRow,
  type CatalogEntryListRow,
} from '@/lib/admin-api';
import { api } from '@/lib/api';
import { usePager } from '@/lib/use-pager';

// ─── API shapes ───────────────────────────────────────────────────────

export interface VehicleRequestRow {
  id: number;
  vendorId: number;
  vendorEmail: string;
  make: string;
  model: string;
  year: number | null;
  trim: string | null;
  notes: string | null;
  status: string;
  createdAt: string;
}

interface VehicleRequestsResponse {
  requests: VehicleRequestRow[];
  total: number;
  page: number;
  limit: number;
}

type ResolveBody =
  | { status: 'fulfilled'; entryId: number }
  | { status: 'rejected'; reason: string };

function fetchVehicleRequests(
  params: { page: number; limit: number },
  signal?: AbortSignal,
): Promise<VehicleRequestsResponse> {
  return api<VehicleRequestsResponse>({
    path: `/admin/vehicle-catalog/requests?status=pending&page=${params.page}&limit=${params.limit}`,
    signal,
  });
}

function resolveRequest(id: number, body: ResolveBody): Promise<{ ok: true }> {
  return api<{ ok: true }>({
    path: `/admin/vehicle-catalog/requests/${id}/resolve`,
    method: 'POST',
    body,
  });
}

// ─── Root component ──────────────────────────────────────────────────

export function VehicleRequests() {
  const { page, limit, pagination } = usePager();

  const requestsQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'requests', 'pending', page, limit],
    queryFn: ({ signal }) => fetchVehicleRequests({ page, limit }, signal),
  });

  // Local state: remove a row after a successful resolution.
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  const requests = (requestsQ.data?.requests ?? []).filter(
    (r) => !dismissed.has(r.id),
  );
  const total = requestsQ.data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Vehicle Catalog"
        title="Vehicle requests"
        sub="Vendor-submitted vehicle requests awaiting review. Create the vehicle in Vehicle Catalog first, then fulfil here."
      />

      {requestsQ.error ? (
        <ErrorPanel message={(requestsQ.error as Error).message} />
      ) : null}

      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-4">
        {requestsQ.isLoading ? (
          <div className="text-[12.5px] text-[color:var(--bo-mute)]">Loading requests…</div>
        ) : requests.length === 0 && !requestsQ.isLoading ? (
          <div className="text-[12.5px] text-[color:var(--bo-mute)]">
            {requestsQ.data
              ? 'No pending vehicle requests — queue is clear.'
              : 'No data.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-[color:var(--bo-line)] text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                  <th className="pb-1.5 pe-3 text-start font-medium">Vendor</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Make</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Model</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Year / Trim</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Notes</th>
                  <th className="pb-1.5 pe-3 text-start font-medium">Submitted</th>
                  <th className="pb-1.5 text-start font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--bo-line)]">
                {requests.map((row) => (
                  <RequestRow
                    key={row.id}
                    row={row}
                    onResolved={() =>
                      setDismissed((prev) => new Set([...prev, row.id]))
                    }
                  />
                ))}
              </tbody>
            </table>
            {/* Custom table, not a DataTable — the same pager control, used
                directly, so the two screens cannot drift. */}
            {total > 0 ? (
              <div className="-mx-4 -mb-4 mt-3 border-t border-[color:var(--bo-line)]">
                <TablePager {...pagination(total)} />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Per-request row with inline fulfil/reject controls ──────────────

function RequestRow({
  row,
  onResolved,
}: {
  row: VehicleRequestRow;
  onResolved: () => void;
}) {
  const [mode, setMode] = useState<'idle' | 'fulfil' | 'reject'>('idle');
  const [selectedMakeId, setSelectedMakeId] = useState<number | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const makesQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'makes'],
    queryFn: ({ signal }) => fetchCatalogMakes(signal),
    staleTime: 60_000,
    enabled: mode === 'fulfil',
  });

  const modelsQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'models', selectedMakeId],
    queryFn: ({ signal }) => fetchCatalogModels(selectedMakeId!, signal),
    enabled: mode === 'fulfil' && selectedMakeId != null,
    staleTime: 60_000,
  });

  const entriesQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'entries', selectedMakeId, selectedModelId],
    queryFn: ({ signal }) =>
      fetchCatalogEntries(
        { makeId: selectedMakeId ?? undefined, modelId: selectedModelId ?? undefined },
        signal,
      ),
    enabled: mode === 'fulfil' && selectedModelId != null,
    staleTime: 60_000,
  });

  const resolveMut = useMutation({
    mutationFn: (body: ResolveBody) => resolveRequest(row.id, body),
    onSuccess: onResolved,
  });

  const makes = makesQ.data?.rows ?? [];
  const models = modelsQ.data?.rows ?? [];
  const entries = entriesQ.data?.rows ?? [];

  const submittedDate = row.createdAt
    ? new Date(row.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  return (
    <tr className="group align-top">
      <td className="py-2.5 pe-3 text-[color:var(--bo-ink)]">
        <span className="font-medium">{row.vendorEmail}</span>
        <br />
        <span className="text-[11px] tabular-nums text-[color:var(--bo-mute)]">
          #{row.vendorId}
        </span>
      </td>
      <td className="py-2.5 pe-3 text-[color:var(--bo-ink)]">{row.make}</td>
      <td className="py-2.5 pe-3 text-[color:var(--bo-ink)]">{row.model}</td>
      <td className="py-2.5 pe-3 tabular-nums text-[color:var(--bo-mute)]">
        {row.year ?? '—'}
        {row.trim ? (
          <span className="ms-1 text-[11px]">· {row.trim}</span>
        ) : null}
      </td>
      <td className="py-2.5 pe-3 max-w-[200px] text-[color:var(--bo-mute)]">
        {row.notes ? (
          <span className="line-clamp-2">{row.notes}</span>
        ) : (
          <span className="text-[color:var(--bo-mute)]">—</span>
        )}
      </td>
      <td className="py-2.5 pe-3 tabular-nums text-[color:var(--bo-mute)]">
        {submittedDate}
      </td>
      <td className="py-2.5">
        {mode === 'idle' ? (
          <div className="flex items-center gap-2">
            <RowAction
              kind="primary"
              onClick={() => setMode('fulfil')}
            >
              Fulfil
            </RowAction>
            <RowAction
              kind="danger"
              onClick={() => setMode('reject')}
            >
              Reject
            </RowAction>
          </div>
        ) : mode === 'fulfil' ? (
          <div className="flex flex-wrap items-center gap-2">
            {/* Make selector */}
            <MakeSelect
              makes={makes}
              loading={makesQ.isLoading}
              value={selectedMakeId}
              onChange={(id) => {
                setSelectedMakeId(id);
                setSelectedModelId(null);
                setSelectedEntryId(null);
              }}
            />

            {/* Model selector — shown once a make is picked */}
            {selectedMakeId != null && (
              <ModelSelect
                models={models}
                loading={modelsQ.isLoading}
                value={selectedModelId}
                onChange={(id) => {
                  setSelectedModelId(id);
                  setSelectedEntryId(null);
                }}
              />
            )}

            {/* Entry selector — shown once a model is picked */}
            {selectedModelId != null && (
              <EntrySelect
                entries={entries}
                loading={entriesQ.isLoading}
                value={selectedEntryId}
                onChange={setSelectedEntryId}
              />
            )}

            {/* Fulfil button — active once an entry is selected */}
            {selectedEntryId != null && (
              <RowAction
                kind="primary"
                disabled={resolveMut.isPending}
                busy={resolveMut.isPending}
                onClick={() =>
                  resolveMut.mutate({ status: 'fulfilled', entryId: selectedEntryId })
                }
              >
                Fulfil
              </RowAction>
            )}

            <RowAction kind="ghost" onClick={() => setMode('idle')}>
              Cancel
            </RowAction>

            {resolveMut.error ? (
              <span className="text-[11px] text-[#9f1239]">
                {(resolveMut.error as Error).message}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              placeholder="Rejection reason…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="h-8 min-w-[180px] rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] placeholder:text-[color:var(--bo-mute)]"
            />

            <RowAction
              kind="danger"
              disabled={resolveMut.isPending || rejectReason.trim() === ''}
              busy={resolveMut.isPending}
              onClick={() =>
                resolveMut.mutate({ status: 'rejected', reason: rejectReason.trim() })
              }
            >
              Reject
            </RowAction>

            <RowAction kind="ghost" onClick={() => setMode('idle')}>
              Cancel
            </RowAction>

            {resolveMut.error ? (
              <span className="text-[11px] text-[#9f1239]">
                {(resolveMut.error as Error).message}
              </span>
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Cascade select primitives (mirrored from NeedsLinking) ──────────

function MakeSelect({
  makes,
  loading,
  value,
  onChange,
}: {
  makes: CatalogMakeRow[];
  loading: boolean;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) =>
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }
      disabled={loading}
      className="h-8 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] disabled:opacity-60"
    >
      <option value="">
        {loading ? 'Loading makes…' : 'Make…'}
      </option>
      {makes.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

function ModelSelect({
  models,
  loading,
  value,
  onChange,
}: {
  models: CatalogModelRow[];
  loading: boolean;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) =>
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }
      disabled={loading}
      className="h-8 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] disabled:opacity-60"
    >
      <option value="">
        {loading ? 'Loading models…' : 'Model…'}
      </option>
      {models.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

function EntrySelect({
  entries,
  loading,
  value,
  onChange,
}: {
  entries: CatalogEntryListRow[];
  loading: boolean;
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) =>
        onChange(e.target.value === '' ? null : Number(e.target.value))
      }
      disabled={loading}
      className="h-8 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] disabled:opacity-60"
    >
      <option value="">
        {loading ? 'Loading entries…' : 'Entry…'}
      </option>
      {entries.map((e) => (
        <option key={e.id} value={e.id}>
          {e.defaultTitle} ({e.year}
          {e.trim ? ` · ${e.trim}` : ''})
        </option>
      ))}
    </select>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────

function ErrorPanel({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
    >
      {message}
    </div>
  );
}
